import type { ServiceItem } from '@/cms/fields/types/checkout-page'
import { Banner, type BannerColors } from '@sunwebgroup/uds-ui/banner'
import { SwitchField } from '@sunwebgroup/uds-ui/fields/switch-field'
import { RadioButton } from '@sunwebgroup/uds-ui/radio-button'
import { Tag } from '@sunwebgroup/uds-ui/tag'
import { TitleListCell } from '@sunwebgroup/uds-ui/title-list-cell'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import { cn } from '@sunwebgroup/uds-ui/utils'
import { isEmpty, values } from 'ramda'
import * as React from 'react'
import { useSessionStorage } from 'react-use'

import { useIntl } from '@/intl'
import { CheckoutSection } from '@/scopes/checkout/components/CheckoutSection'
import { SelectableListItem, SelectableRadioButtonGroup } from '@/scopes/checkout/components/SelectableListItem'
import { getIconForService } from '@/scopes/checkout/components/ServiceIcon'
import { useSendAncillaryAvailable } from '@/scopes/checkout/domain/analytics/useSendAncillaryAvailable'
import { useCheckoutSid, useService } from '@/scopes/checkout/domain/hooks'
import { useBreakpoints } from '@/scopes/common/components/utils/breakpoints'
import { BannerType } from '@/scopes/common/domain/types/banner'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { updatePerBookingService, updatePerParticipantService } from '../../domain/actions'
import { createAncillariesClickedEvent, createGenericServiceEvent } from '../../domain/analytics'
import { bannerColorMap, type CheckoutServiceOption, ServiceType } from '../../domain/types'
import styles from './GenericService/GenericService.module.css'
import { AGE_CATEGORY_TRANSLATION_KEY, formatPriceRowText, getPriceForParticipant, getPricesPerAgeCategory, type PerBookingPriceRow } from './priceHelpers'
import serviceStyles from './ServiceComponents.module.css'

export function TravelInsuranceService({ item }: { item: ServiceItem }) {
  const intl = useIntl()
  const sid = useCheckoutSid()
  const { service } = useService(ServiceType.TravelInsurance)
  const { service: perBookingService, setServiceValue } = useService(ServiceType.TravelInsurancePerBooking)
  const { componentTitle, componentDescription, popupTitle, popupDescription, popupReadMoreLabel, serviceConfigurations } = item

  const { showToggleForAllParticipants, toggleForAllParticipantsCheckedByDefault, toggleForAllParticipantsLabel } = serviceConfigurations || {}

  const { sendEvent } = useAnalytics()

  const [selectPerParticipantChecked, setSelectPerParticipantChecked] = useSessionStorage(
    `${sid}-travel-insurance-is-per-participant`,
    toggleForAllParticipantsCheckedByDefault,
  )

  const handleSwitchPerParticipantChange = React.useCallback(
    () => {
      setSelectPerParticipantChecked(!selectPerParticipantChecked)
      sendEvent(
        createGenericServiceEvent({
          description: 'Choose per participant',
          element: 'toggle',
          type: 'selectTravelInsurance',
          result: !selectPerParticipantChecked ? 'element selected' : 'element unselected',
        }),
      )

      if (!selectPerParticipantChecked || !perBookingService)
        return

      const participants = Object.entries(service?.participants || {}).sort(
        ([keyA], [keyB]) => keyA.localeCompare(keyB),
      )

      const selectedValue = participants.find(([, value]) => value?.selectedValue)?.[1]?.selectedValue

      if (!selectedValue)
        return

      setServiceValue(updatePerBookingService({ value: selectedValue })(perBookingService))
    },
    [service, perBookingService, setServiceValue, setSelectPerParticipantChecked, selectPerParticipantChecked, sendEvent],
  )

  React.useEffect(() => {
    if (!service)
      return
    if (service.participants && Object.keys(service.participants).length === 1) {
      setSelectPerParticipantChecked(false)
    }
  })
  useSendAncillaryAvailable(service, 'selectTravelInsurance')

  if (!service)
    return null

  return (
    <CheckoutSection
      title={componentTitle || intl.formatMessage({ id: 'checkout_steps_ancillaries_travel_insurance_title' })}
      description={componentDescription}
      glyph={getIconForService(ServiceType.TravelInsurance)}
      moreInformationTitle={popupTitle}
      moreInformationDescription={popupDescription}
      moreInformationReadMoreLabel={popupReadMoreLabel}
    >
      <div className={cn(styles.flexContent, styles.flexContentGap12)}>
        {showToggleForAllParticipants && service.participants && Object.keys(service.participants).length > 1 && (
          <SwitchField
            checked={selectPerParticipantChecked}
            label={toggleForAllParticipantsLabel || intl.formatMessage({ id: 'checkout_steps_ancillaries_generic_service_applies_to_all_participants' })}
            onCheckedChange={handleSwitchPerParticipantChange}
            variant="transparent"
            id="travel-insurance-select-per-participant"
          />
        )}
        {selectPerParticipantChecked ? <TravelInsuranceServicePerParticipant /> : <TravelInsuranceServicePerBooking />}
      </div>
    </CheckoutSection>
  )
}

function TravelInsuranceServicePerParticipant() {
  const { service, setServiceValue } = useService(ServiceType.TravelInsurance)
  const { sendEvent } = useAnalytics()

  const handleValueChange = React.useCallback(
    (participantId: string, value: string) => {
      if (!service)
        return
      setServiceValue(updatePerParticipantService({ participantId, value })(service))
      const optionClicked = service.options[value]
      sendEvent(
        createAncillariesClickedEvent({
          result: optionClicked?.price || 0,
          type: optionClicked?.label ? `${optionClicked?.label} for participant ${participantId}` : '',
          description: 'selectTravelInsurance',
          element: 'radiobutton',
        }),
      )
    },
    [sendEvent, setServiceValue, service],
  )

  if (!service)
    return null

  const { options, rooms, participants, banners } = service

  return (
    <div className={cn(styles.flexContent, styles.flexContentGap12)}>
      {rooms.map(({ id: roomId, name: roomName, participantIds }) => (
        <div key={roomId} className={cn(styles.flexContent, styles.flexContentGap16)}>
          {rooms.length > 1 && roomName
            ? (
                <Typography as="p" className={cn(serviceStyles.roomName)}>
                  {roomName}
                </Typography>
              )
            : null}
          {participantIds.map((participantId) => {
            const participant = participants?.[participantId]
            if (!participant || participant.availableOptions.length === 0) {
              return null
            }

            return (
              <ParticipantRow
                {...participant}
                key={participantId}
                options={options}
                onValueChange={handleValueChange}
                showName={values(participants).length > 1}
              />
            )
          })}
        </div>
      ))}
      {isEmpty(rooms)
      && Object.values(participants ?? {}).map(participant => (
        <ParticipantRow {...participant} key={participant.id} options={options} onValueChange={handleValueChange} showName={values(participants).length > 1} />
      ))}
      {banners.map(({ title, text, type = 'info' }, index) => {
        return (
          <Banner
            key={`banner-${index}`}
            color={bannerColorMap[type as BannerType] as BannerColors}
            size="M"
          >
            <Banner.TextsWrapper>
              {title && <Banner.Text>{title}</Banner.Text>}
              {text && <Banner.SupportingText innerHTML={text} />}
            </Banner.TextsWrapper>
          </Banner>
        )
      },
      )}
    </div>
  )
}

function ParticipantRow({
  id,
  options,
  availableOptions,
  selectedValue,
  name,
  onValueChange,
  showName = true,
}: {
  id: string
  options: Record<string, CheckoutServiceOption>
  availableOptions: string[]
  name: string
  selectedValue: string | null
  onValueChange: (participantId: string, value: string) => void
  showName?: boolean
}) {
  const intl = useIntl()

  const handleValueChange = React.useCallback(
    (value: string) => {
      onValueChange(id, value)
    },
    [id, onValueChange],
  )

  return (
    <div className={cn(styles.flexContent, styles.flexContentGap8)}>
      {showName && (
        <Typography className={cn(serviceStyles.participantName)}>
          {name}
        </Typography>
      )}
      <SelectableRadioButtonGroup value={selectedValue ?? undefined} onValueChange={handleValueChange}>
        <div className={cn(styles.flexContent, styles.flexContentGap8)}>
          {(availableOptions ?? []).map((optionId) => {
            const option = options[optionId]
            const { value, label, badge } = option
            const participantPrice = getPriceForParticipant(option, id)
            return (
              <SelectableListItem key={value} value={value} className={cn(styles.itemOptionWrapper)}>
                <div className={cn(styles.itemOption)}>
                  <div className={cn(styles.itemOptionControl)}>
                    <RadioButton value={value} data-qa="travelins-service-per-participant-option" />
                  </div>
                  <div className={cn(styles.itemOptionContent)}>
                    {badge && <Tag variant="success-tinted" size="M" className={cn(styles.itemOptionTag)}>{badge}</Tag>}
                    <TitleListCell
                      size="L"
                      title={label}
                    />
                  </div>
                  <div className={cn(styles.itemOptionTrailing)}>
                    <span className={cn(styles.optionPrice)}>{intl.formatCurrency(participantPrice, { showSign: participantPrice < 0 })}</span>
                  </div>
                </div>
              </SelectableListItem>
            )
          },
          )}
        </div>
      </SelectableRadioButtonGroup>
    </div>
  )
}

function TravelInsuranceServicePerBooking() {
  const intl = useIntl()
  const { service, setServiceValue } = useService(ServiceType.TravelInsurancePerBooking)
  const { sendEvent } = useAnalytics()
  const { isMobile } = useBreakpoints()

  const getSubtitleOption = (rows: PerBookingPriceRow[]) => {
    if (rows.length === 0 || rows.every(({ prices }) => prices.every(price => price === 0)))
      return ''

    return rows
      .map(({ ageCategory, prices }) =>
        `${intl.formatMessageIfExists({ id: AGE_CATEGORY_TRANSLATION_KEY[ageCategory] })} ${formatPriceRowText(prices, price => intl.formatMessage(
          { id: 'checkout_steps_price_per_person' },
          { price: intl.formatCurrency(price, { showSign: price < 0 }) },
        ), (minimumPrice, maximumPrice) => intl.formatMessage(
          { id: 'checkout_steps_price_per_person_range' },
          {
            minimumPrice: intl.formatCurrency(minimumPrice, { showSign: minimumPrice < 0 }),
            maximumPrice: intl.formatCurrency(maximumPrice, { showSign: maximumPrice < 0 }),
          },
        ))}`)
      .join(', ')
  }

  const handleValueChange = React.useCallback(
    (value: string) => {
      if (!service)
        return
      setServiceValue(updatePerBookingService({ value })(service))
      const optionClicked = service.options.find(option => option.value === value)
      sendEvent(
        createAncillariesClickedEvent({
          result: (Object.keys(service.participants).length) * (optionClicked?.price || 0),
          type: optionClicked?.label || '',
          description: 'selectTravelInsurance',
          element: 'radiobutton',
        }),
      )
    },
    [sendEvent, setServiceValue, service],
  )

  if (!service)
    return null

  const { options, selectedValue, banners, participants } = service

  return (
    <div className={cn(styles.flexContent, styles.flexContentGap12)}>
      <SelectableRadioButtonGroup value={selectedValue ?? undefined} onValueChange={handleValueChange}>
        <div className={cn(styles.optionItemsContainer)}>
          {options.map((option) => {
            const { label, badge, price, value } = option
            const priceRows = getPricesPerAgeCategory(option, participants)
            return (
              <SelectableListItem key={value} value={value} className={cn(styles.itemOptionWrapper)}>
                <div className={cn(styles.itemOption)}>
                  <div className={cn(styles.itemOptionControl)}>
                    <RadioButton value={value} data-qa="travelins-service-per-booking-option" />
                  </div>
                  <div className={cn(styles.itemOptionContent)}>
                    {badge && <Tag variant="success-tinted" size="M" className={cn(styles.itemOptionTag)}>{badge}</Tag>}
                    { !isMobile && (
                      <span className={cn(styles.optionValue)}>{label}</span>
                    )}
                    { isMobile && (
                      <TitleListCell
                        size="L"
                        title={label}
                        subtitle={getSubtitleOption(priceRows)}
                      />
                    )}
                  </div>
                  <div className={cn(styles.itemOptionTrailing)}>
                    {!isMobile && priceRows.length > 0
                      ? (
                          priceRows.map(({ ageCategory, prices }) => (
                            <span key={ageCategory} className={cn(styles.optionPrice)}>
                              {intl.formatMessageIfExists({ id: AGE_CATEGORY_TRANSLATION_KEY[ageCategory] })}
                              {' '}
                              {formatPriceRowText(
                                prices,
                                rowPrice => intl.formatMessage({ id: 'checkout_steps_price_per_person' }, { price: intl.formatCurrency(rowPrice, { showSign: rowPrice < 0 }) }),
                                (minimumPrice, maximumPrice) => intl.formatMessage(
                                  { id: 'checkout_steps_price_per_person_range' },
                                  {
                                    minimumPrice: intl.formatCurrency(minimumPrice, { showSign: minimumPrice < 0 }),
                                    maximumPrice: intl.formatCurrency(maximumPrice, { showSign: maximumPrice < 0 }),
                                  },
                                ),
                              )}
                            </span>
                          ))
                        )
                      : (
                          <span className={cn(styles.optionPrice, styles.optionPriceSinglePrice)}>
                            {intl.formatMessage({ id: 'checkout_steps_price_per_person' }, { price: intl.formatCurrency(price, { showSign: price < 0 }) })}
                          </span>
                        )}
                  </div>
                </div>
              </SelectableListItem>
            )
          })}
        </div>
      </SelectableRadioButtonGroup>
      {banners.map(({ title, text, type = 'info' }, index) => {
        return (
          <Banner
            key={`banner-${index}`}
            color={bannerColorMap[type as BannerType] as BannerColors}
            size="M"
          >
            <Banner.TextsWrapper>
              {title && <Banner.Text>{title}</Banner.Text>}
              {text && <Banner.SupportingText innerHTML={text} />}
            </Banner.TextsWrapper>
          </Banner>
        )
      },
      )}
    </div>
  )
}
